<script setup lang="ts">
import { useUpdateDtwinConfiguration } from "../composables/useCreateDtwinConfiguration";
import { useUploadDtwinConfiguration } from "../composables/useUploadDtwinConfiguration";
import type LxcSideCanvas from "@lxc/app-device-common/src/components/LxcSideCanvas.vue";
import type { Rules } from "async-validator";
import { HttpStatusCode } from "axios";
import { storeToRefs } from "pinia";
import { type ComputedRef, ref, type Ref } from "vue";
import LxcError from "~/core/models/LxcError.class";
import type { UploadUserInformationI } from "~/core/models/UploadFile.interface";
import {
  NotificationKey,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import type { FilesUploadErrorMessagesI } from "~/core/utils/upload.utils";
import uploadUtils from "~/core/utils/upload.utils";
import type { DtwinConfigurationForm } from "~/modules/dtwin/models/DtwinConfigurationForm.interface";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import ILxcPauseCircle from "~icons/lxc/pause-circle";

const props = defineProps<{
  showImportPanel: boolean;
  dtwinUid: string;
  configurationType: string;
}>();

const emit = defineEmits(["update:showImportPanel", "uploaded"]);

const { t } = useI18n();

const { currentOrganization } = storeToRefs(useOrganizationStore());

const {
  uploadDtwinConfiguration,
  error: errorUploadDtwinConfiguration,
  result: resultUploadDtwinConfiguration,
} = useUploadDtwinConfiguration({
  organizationCode: currentOrganization.value?.code,
  dtwinUid: props.dtwinUid,
});

const { updateDtwinConfiguration, error: errorUpdateDtwinConfiguration } =
  useUpdateDtwinConfiguration({
    organizationCode: currentOrganization.value?.code,
  });

const sideCanvasShown = computed({
  get: () => {
    return props.showImportPanel;
  },
  set: (shown: boolean) => {
    emit("update:showImportPanel", shown);
  },
});

const configurationTypeLabel = computed(() =>
  t(
    `dtwins.form.configuration.information.configurationType.${props.configurationType}`,
  ),
);

const defaultConfigurationForm = {
  versionName: "",
  version: "",
};

const configurationForm = ref<DtwinConfigurationForm>({
  ...defaultConfigurationForm,
});

const configurationFormRef: Ref<HTMLFormElement | undefined | null> = ref();

const sideCanvasRef: Ref<typeof LxcSideCanvas | undefined> = ref();

const rules: Rules = {
  versionName: [
    {
      required: true,
      message: t("input.error.required"),
      whitespace: true,
    },
    {
      max: 50,
      message: t("input.error.maxLength", { maxLength: 50 }),
    },
  ],
  version: [
    { required: true, message: t("input.error.required"), whitespace: true },
  ],
};

const isFormValid: ComputedRef<boolean | undefined> = computed(
  () => configurationFormRef.value?.isValid,
);

const files: Ref<FileList | undefined | null> = ref();

const fileProgress: Ref<number | undefined | null> = ref();

const filesUploadError: Ref<Error | undefined | null> = ref();

const userInformation: UploadUserInformationI = {
  title: t("dtwins.form.configuration.upload.userInformation.title"),
  description: t(
    "dtwins.form.configuration.upload.userInformation.description",
  ),
};
const fileFormats = [".json", ".iso"];

const MAX_FILE_SIZE_BYTES_ALLOWED = 750_000_000;

const errorMessages: FilesUploadErrorMessagesI = {
  invalidType: t("dtwins.form.configuration.upload.error.invalidFileType"),
  size: t("dtwins.form.configuration.upload.error.fileSize"),
  empty: t("dtwins.form.configuration.upload.error.emptyFile"),
};

function resetSelectedFile() {
  files.value = undefined;
  fileProgress.value = undefined;
  filesUploadError.value = undefined;
}

function onCancel() {
  sideCanvasShown.value = false;
}

const contentResourceUid: Ref<string | undefined> = ref();

async function uploadConfiguration() {
  filesUploadError.value = undefined;

  if (files.value?.length) {
    const canUploadFiles = !Array.from(files.value).find(
      (file: File) =>
        !uploadUtils.canUploadFile(
          file,
          MAX_FILE_SIZE_BYTES_ALLOWED,
          fileFormats,
          errorMessages,
          filesUploadError,
        ),
    );

    if (canUploadFiles) {
      await uploadDtwinConfiguration(files.value, fileProgress);

      if (LxcError.check(errorUploadDtwinConfiguration.value)) {
        if (
          errorUploadDtwinConfiguration.value.status === HttpStatusCode.Conflict
        ) {
          filesUploadError.value = new Error(
            t("dtwins.form.configuration.upload.error.fileAlreadyExist"),
          );
        } else {
          filesUploadError.value = errorUploadDtwinConfiguration.value.toError(
            NotificationKey.uploadError,
          );
        }
      } else {
        contentResourceUid.value = resultUploadDtwinConfiguration.value?.uid;

        // get the configuration version name from the descriptor of the manifest file
        configurationForm.value.versionName =
          resultUploadDtwinConfiguration.value?.files?.find(
            (file) => file.descriptor?.configurationVersionName,
          )?.descriptor?.configurationVersionName || "";

        // get the configuration version from the descriptor of the manifest file
        configurationForm.value.version =
          resultUploadDtwinConfiguration.value?.files?.find(
            (file) => file.descriptor?.configurationVersion,
          )?.descriptor?.configurationVersion || "";
      }
    }
  }
}

async function addConfiguration() {
  if (isFormValid.value && contentResourceUid.value) {
    await updateDtwinConfiguration(contentResourceUid.value, {
      ...configurationForm.value,
      type: props.configurationType,
    });

    if (errorUpdateDtwinConfiguration.value) {
      errorUpdateDtwinConfiguration.value.notify(NotificationKey.saveError);
    } else {
      sideCanvasShown.value = false;

      showNotificationSuccess(t("dtwins.form.configuration.upload.success"));

      resetSelectedFile();

      emit("uploaded");
    }
  }
}

function onCancelClick() {
  sideCanvasRef.value?.hide();
}

// on selected file changed
watch(files, () => {
  // reset the uploaded configuration
  resultUploadDtwinConfiguration.value = undefined;

  // reset the configuration form
  configurationForm.value = { ...defaultConfigurationForm };
  setTimeout(configurationFormRef.value?.clearValidate, 0);
});
</script>

<template>
  <lxc-side-canvas
    ref="sideCanvasRef"
    v-model:show="sideCanvasShown"
    :header="t('dtwins.form.configuration.upload.title')"
    :close-tooltip="t('filters.close')"
    :confirm-enabled="files?.length"
    :confirm-title="t('dtwins.form.configuration.upload.confirm.title')"
    :confirm-message="t('dtwins.form.configuration.upload.confirm.message')"
    :confirm-ok-label="t('button.confirm')"
    :confirm-cancel-label="t('button.cancel')"
    :confirm-icon="ILxcPauseCircle"
    confirm-icon-color-theme="error"
    confirm-color-theme="danger"
    @discard="onCancel"
    @hidden="resetSelectedFile"
  >
    <upload-file
      v-model:files="files"
      v-model:files-progress="fileProgress"
      v-model:files-upload-error="filesUploadError"
      :user-information="userInformation"
      :file-formats="fileFormats"
      multiple
    />
    <div v-if="resultUploadDtwinConfiguration" class="mt-8">
      <h6 class="mb-4">
        {{ t("dtwins.form.configuration.upload.form.title") }}
      </h6>
      <lxc-form
        ref="configurationFormRef"
        :model="configurationForm"
        :rules="rules"
        class="flex flex-col gap-2"
      >
        <lxc-form-item
          :label="t('dtwins.form.configuration.upload.form.versionName')"
          prop="versionName"
        >
          <lxc-input v-model="configurationForm.versionName" type="text" />
        </lxc-form-item>
        <lxc-form-item
          :label="t('dtwins.form.configuration.upload.form.version')"
          prop="version"
        >
          <lxc-input v-model="configurationForm.version" type="text" />
        </lxc-form-item>
        <lxc-form-item
          :label="t('dtwins.form.configuration.upload.form.type')"
          prop="type"
          read-only
        >
          <lxc-input v-model="configurationTypeLabel" type="text" />
        </lxc-form-item>
      </lxc-form>
    </div>
    <template #footer>
      <div class="grid grid-cols-[max-content_auto] gap-4">
        <lxc-button
          html-type="button"
          type="secondary"
          :title="t('button.cancel')"
          @click="onCancelClick"
        >
          {{ t("button.cancel") }}
        </lxc-button>
        <lxc-button
          v-if="files?.length && !resultUploadDtwinConfiguration"
          html-type="button"
          :disabled="!!filesUploadError"
          :title="t('button.upload')"
          @click="uploadConfiguration"
        >
          {{ t("button.upload") }}
        </lxc-button>
        <lxc-button
          v-if="resultUploadDtwinConfiguration"
          html-type="button"
          :disabled="!!filesUploadError || !isFormValid"
          :title="t('button.validate')"
          @click="addConfiguration"
        >
          {{ t("button.validate") }}
        </lxc-button>
      </div>
    </template>
  </lxc-side-canvas>
</template>
